import React, { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
// import logo from "../../assets/images/logo.png";
import logo from "../../assets/images/redlogo.png";
import eye from "../../assets/icons/eye.svg";
import eyeslash from "../../assets/icons/eye-slash.svg";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserDetail, updateUserPassword } from "../../service/UserService";
import LayoutBg from "../../assets/images/layoutBg.png";

const formInitialValues = {
  userUid: 0,
  newPassword: "",
  confirmPassword: "",
  showNewPassword: false,
  showConfirmPassword: false,
};

export default function SetPassword() {
  const [formValues, setFormValues] = useState(formInitialValues);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const navigate = useNavigate();
  const [isDataLoading, setDataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAlert, setAlertMessage] = useState({
    isVisible: false,
    type: "",
    message: "",
  });

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  let urlParams = useQuery();

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string().required("New password is required."),
    confirmPassword: Yup.string()
      .required("Confirm password is required.")
      .oneOf(
        [Yup.ref("newPassword")],
        "Confirm password doesn't match with the new password."
      ),
  });

  const handleChange = (field: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" })); // clear error as user types
  };

  const handleClickShowNewPassword = () => {
    setFormValues((prev) => ({
      ...prev,
      showNewPassword: !prev.showNewPassword,
    }));
  };

  const handleClickShowConfirmPassword = () => {
    setFormValues((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));
  };

  async function fetchuserDetail(forgot_password_token: string) {
    setDataLoading(true);
    try {
      let params = new URLSearchParams();
      params.append("token", forgot_password_token.toString());
      const userDetail: any = await getUserDetail(params);
      if (
        userDetail?.ok === true &&
        userDetail.data?.data !== null &&
        userDetail.data?.data !== undefined
      ) {
        let data = userDetail.data.data;
        setFormValues((prev) => ({
          ...prev,
          userUid: data.UID,
        }));
      } else {
        setAlertMessage({
          isVisible: true,
          type: "error",
          message: userDetail?.data?.message || "User not found",
        });
      }
    } catch (error: any) {
      setAlertMessage({
        isVisible: true,
        type: "error",
        message: error?.message || "Something went wrong",
      });
    } finally {
      setDataLoading(false);
    }
  }

  async function submitSetPasswordForm(e: React.FormEvent) {
    e.preventDefault();

    try {
      await validationSchema.validate(formValues, { abortEarly: false });
    } catch (validationError: any) {
      const newErrors: { [key: string]: string } = {};
      validationError.inner.forEach((err: any) => {
        if (err.path) newErrors[err.path] = err.message;
      });
      setFormErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const user: any = await updateUserPassword({
        userUid: formValues.userUid,
        password: formValues.newPassword,
      });
      if (user?.data === true) {
        setAlertMessage({
          isVisible: true,
          type: "success",
          message: "Password changed successfully.",
        });
        setFormValues(formInitialValues);
        setTimeout(() => {
          navigate("/sign-in");
        }, 2000);
      } else {
        setAlertMessage({
          isVisible: true,
          type: "error",
          message: "Something went wrong!",
        });
      }
    } catch (error: any) {
      setAlertMessage({
        isVisible: true,
        type: "error",
        message: error?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setAlertMessage({ isVisible: false, type: "", message: "" });
      }, 7000);
    }
  }

  useEffect(() => {
    let forgot_password_token = urlParams.get("t");
    if (forgot_password_token) {
      fetchuserDetail(forgot_password_token);
    }
  }, []);

  return (
    <>
      {/* <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isDataLoading}
      >
        <img
          loading="lazy"
          alt="imprend"
          src="/loader.gif"
          style={{ height: "250px", width: "auto" }}
        />
      </Backdrop> */}

      <Grid
        container
        sx={{
          position: "relative",
          backgroundColor: "#E5E5E5",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          height: "100%",
          width: "100%",
          minWidth: "991px",
          alignItems: "center",
          minHeight: "100vh",

          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${LayoutBg})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.1,
            zIndex: 0,
          },

          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        }}
      >
        <Box>
          <Grid size={12} textAlign="center" mb={2}>
            <a href="https://www.stakeholdermaps.com/">
              <img src={logo} alt="logo" style={{ maxWidth: 200 }} />
            </a>
          </Grid>
          <Paper elevation={3} className="paper-singin">
            <Grid sx={{ width: "100%", opacity: 1 }}>
              <form onSubmit={submitSetPasswordForm}>
                <Box className="box-container" sx={{ my: 2 }}>
                  <Paper className="paper-container">
                    {showAlert.isVisible && (
                      <Grid marginBottom={1}>
                        <Alert
                          severity={
                            showAlert.type === "success" ? "success" : "error"
                          }
                        >
                          {showAlert.message}
                        </Alert>
                      </Grid>
                    )}

                    <Typography sx={{ textAlign: "left" }}>
                      New password
                    </Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      placeholder="New password"
                      value={formValues.newPassword}
                      onChange={(e) =>
                        handleChange("newPassword", e.target.value)
                      }
                      error={!!formErrors.newPassword}
                      helperText={formErrors.newPassword}
                      type={formValues.showNewPassword ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowNewPassword}
                              edge="end"
                            >
                              <img
                                loading="lazy"
                                className="set-password-eye-icon"
                                src={
                                  formValues.showNewPassword ? eyeslash : eye
                                }
                                alt="toggle"
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Typography sx={{ textAlign: "left", marginTop: 3 }}>
                      Confirm password
                    </Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      placeholder="Confirm password"
                      value={formValues.confirmPassword}
                      onChange={(e) =>
                        handleChange("confirmPassword", e.target.value)
                      }
                      error={!!formErrors.confirmPassword}
                      helperText={formErrors.confirmPassword}
                      type={
                        formValues.showConfirmPassword ? "text" : "password"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowConfirmPassword}
                              edge="end"
                            >
                              <img
                                loading="lazy"
                                className="set-password-eye-icon"
                                src={
                                  formValues.showConfirmPassword
                                    ? eyeslash
                                    : eye
                                }
                                alt="toggle"
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <LoadingButton
                      color="primary"
                      sx={{
                        marginTop: 3,
                        backgroundColor: "#4C0808",
                        fontWeight: 400,
                        fontSize: "13px",
                        borderRadius: "4px",
                      }}
                      loading={loading}
                      fullWidth
                      loadingPosition="start"
                      variant="contained"
                      type="submit"
                    >
                      Submit
                    </LoadingButton>
                  </Paper>
                </Box>
              </form>
            </Grid>
          </Paper>
        </Box>
      </Grid>
    </>
  );
}
